import { useEffect, useRef } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

import type { SourceValue } from "@productlane/zero/queries"
import { SOURCE_VALUES } from "@productlane/zero/queries"

import type { NumericFilter, NumericOperator } from "@/components/filters"
import {
	deserializeNegations,
	NEGATION_PARAM,
	serializeNegations,
} from "@/components/filters"
import { useCompaniesFilterState } from "./companies-filter-state"

const NULL_TOKEN = "_null"

const KEYS = {
	tiers: "tiers",
	statuses: "statuses",
	ownerIds: "owners",
	revenue: "revenue",
	size: "size",
	sources: "source",
} as const

const NEGATABLE_KEYS = {
	tiers: KEYS.tiers,
	statuses: KEYS.statuses,
	ownerIds: KEYS.ownerIds,
	sources: KEYS.sources,
} as const

const ALL_KEYS = [...Object.values(KEYS), NEGATION_PARAM] as const
const FILTER_KEY_SET = new Set<string>(ALL_KEYS)

function serializeArr(values: readonly (string | null)[]): string {
	return values.map((v) => (v === null ? NULL_TOKEN : v)).join(",")
}

function deserializeArr(param: string): Array<string | null> {
	if (!param) return []
	return param.split(",").map((v) => (v === NULL_TOKEN ? null : v))
}

function serializeNumeric(f: NumericFilter | null): string {
	if (!f) return ""
	return `${f.operator}:${f.value}`
}

function deserializeNumeric(param: string): NumericFilter | null {
	if (!param) return null
	const match = param.match(/^(>=|<=|=|!=):(-?\d+\.?\d*)$/)
	if (!match) return null
	return {
		operator: match[1] as NumericOperator,
		value: Number(match[2]),
	}
}

function deserializeSources(param: string): SourceValue[] {
	if (!param) return []
	return param
		.split(",")
		.filter((v): v is SourceValue =>
			(SOURCE_VALUES as readonly string[]).includes(v),
		)
}

function buildParams(): URLSearchParams {
	const s = useCompaniesFilterState.getState()
	const params = new URLSearchParams()
	if (s.tiers.length > 0) params.set(KEYS.tiers, serializeArr(s.tiers))
	if (s.statuses.length > 0) params.set(KEYS.statuses, serializeArr(s.statuses))
	if (s.ownerIds.length > 0) params.set(KEYS.ownerIds, serializeArr(s.ownerIds))
	if (s.revenue) params.set(KEYS.revenue, serializeNumeric(s.revenue))
	if (s.size) params.set(KEYS.size, serializeNumeric(s.size))
	if (s.sources.length > 0) params.set(KEYS.sources, s.sources.join(","))
	const negated = serializeNegations(s.negations, NEGATABLE_KEYS)
	if (negated) params.set(NEGATION_PARAM, negated)
	return params
}

export function useCompaniesFilterUrlSync() {
	const search = useSearch({ strict: false })
	const navigate = useNavigate()
	const isInitialised = useRef(false)

	useEffect(() => {
		if (isInitialised.current) return
		isInitialised.current = true

		const hasFilterParams = ALL_KEYS.some((k) => search[k] !== undefined)
		if (hasFilterParams) {
			const tiers = deserializeArr(search[KEYS.tiers] ?? "").filter(
				(v): v is string => v !== null,
			)
			const statuses = deserializeArr(search[KEYS.statuses] ?? "").filter(
				(v): v is string => v !== null,
			)
			const ownerIds = deserializeArr(search[KEYS.ownerIds] ?? "")
			const revenue = deserializeNumeric(search[KEYS.revenue] ?? "")
			const size = deserializeNumeric(search[KEYS.size] ?? "")
			const sources = deserializeSources(search[KEYS.sources] ?? "")

			useCompaniesFilterState.setState({
				negations: deserializeNegations(
					search[NEGATION_PARAM] ?? "",
					NEGATABLE_KEYS,
				),
				tiers,
				statuses,
				ownerIds,
				revenue,
				size,
				sources,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		const unsub = useCompaniesFilterState.subscribe(() => {
			if (!isInitialised.current) return
			const filterParams = buildParams()
			void navigate({
				to: ".",
				search: (prev) => ({
					...Object.fromEntries(
						Object.entries(prev).filter(([key]) => !FILTER_KEY_SET.has(key)),
					),
					...Object.fromEntries(filterParams),
				}),
				replace: true,
			})
		})
		return unsub
	}, [navigate])
}
